import * as React from 'react';
import { on, off } from '@byted/ecom-mobile-lib/lib/utils/dom';
import './index.scss';

export enum LoadStatus {
    LoadInit = 'loadInit',
    Loading = 'loading',
    LoadOver = 'loadOver'
}

interface IProps {
    // loadStatus: LoadStatus;
    loadingText?: string;
    loadOverText?: string;
    loadInitText?: string;
    offset?: number;
    loadFunc: (...args: any[]) => Promise<unknown>;
    pageSize?: number;
    deadPageSize?: number;
    enableRetry?: boolean; // 当本页条数不足pageSize，会继续尝试下一页获取
    retryTimes?: number;
    /**
     * @description 标识当前loading的状态
     */
    id: string;
    scrollDomId?: string;
    onStatusChange?: (status: string) => unknown;
    onLoadingError?: (error: any) => any;
    keepLoadingWhenError?: boolean;
    outScrollDomId: string;
}

const cachedTab: Record<string, { retryTimes: number; loadStatus: LoadStatus }> = {};
const addCache = (id: string, retryTimes: number = 3) => {
    cachedTab[id] = { retryTimes, loadStatus: LoadStatus.LoadInit };
};

const Loading = (props: IProps) => {
    // 校验缓存状态
    if (!cachedTab[props.id]) {
        addCache(props.id, props.retryTimes);
    }
    React.useEffect(() => {
        addCache(props.id, props.retryTimes);
    }, [props.id]);
    const [loadStatus, changeLoadStatus] = React.useState(cachedTab[props.id].loadStatus);
    const loadingRef = React.useRef(null);
    // const isMount = React.useRef(true);
    const updateStatus = (status: LoadStatus) => {
        cachedTab[props.id].loadStatus = status;
        if (Object.prototype.toString.call(props.onStatusChange) === '[object Function]') {
            props.onStatusChange!(status);
        }
        // if (!isMount.current) return;
        changeLoadStatus(status);
    };
    const scrollHandler = (_event?: Event, isRetry = false) => {
        if (cachedTab[props.id].loadStatus !== LoadStatus.LoadInit) {
            return;
        }
        if (props.enableRetry && cachedTab[props.id].retryTimes <= 0) {
            updateStatus(LoadStatus.LoadOver);

            return;
        }
        if (!loadingRef.current) return;
        const coord = (loadingRef.current! as HTMLElement).getBoundingClientRect();
        const winInnerHeight = window.innerHeight;

        if (winInnerHeight === 0) {
            return;
        }
        // TODO: 这里的逻辑需要重新考虑
        if (coord.top - winInnerHeight < props.offset!) {
            updateStatus(LoadStatus.Loading);
            props
                .loadFunc(isRetry)
                .then((res) => {
                    if ({}.toString.call(res) === '[object Array]') {
                        if ((res as any[]).length < props.deadPageSize!) {
                            if (props.enableRetry) {
                                cachedTab[props.id].retryTimes -= 1;
                                updateStatus(LoadStatus.LoadInit);
                                // eslint-disable-next-line no-undefined
                            } else {
                                updateStatus(LoadStatus.LoadOver);
                            }
                        } else {
                            updateStatus(LoadStatus.LoadInit);
                        }
                    } else {
                        updateStatus(LoadStatus.LoadOver);
                    }
                })
                .catch((err: unknown) => {
                    if (props.onLoadingError) {
                        props.onLoadingError(err);
                    }
                    if (props.keepLoadingWhenError) {
                        cachedTab[props.id].retryTimes -= 1;
                        updateStatus(LoadStatus.LoadInit);
                    } else {
                        updateStatus(LoadStatus.LoadOver);
                    }
                });
        }
    };

    React.useEffect(() => {
        scrollHandler();
        const dom$ =
            props.scrollDomId && document.getElementById(props.scrollDomId)
                ? document.getElementById(props.scrollDomId)
                : ((window as unknown) as Element);
        if (dom$) {
            on(dom$, 'scroll', scrollHandler);
        }
        return () => {
            // isMount.current = false;
            if (dom$) {
                off(dom$!, 'scroll', scrollHandler);
            }
        };
    }, [props.loadFunc]);

    return (
        <div ref={loadingRef} className="loading">
            {(props as any)[`${loadStatus}Text`]}
        </div>
    );
};

Loading.defaultProps = {
    loadingText: '正在加载...',
    loadOverText: '没有更多了',
    loadInitText: '加载更多',
    offset: 300,
    pageSize: 10,
    deadPageSize: 3,
    enableRetry: true,
    retryTimes: 3,
    scrollDomId: null,
    keepLoadingWhenError: true,
    outScrollDomId: ''
};

Loading.whyDidYouRender = false;

export default Loading;
